import { BaseHttpAdapter } from '~/services/adapters/base-http.adapter';
import {
    IPicTimeConnectedGallery,
    IPicTimeGallery,
    TGalleryDetailsPayload,
    IPicTimeAccountData,
    IPicTimeProjectTemplate
} from '~/apps/main/PicTime/pic-time.types';
import {
    IPicTimeAuthUrl,
    IPicTimeIntegrationInfoResponse
} from '~/types/server.types';

export class PicTimeAdapter extends BaseHttpAdapter {
    getPicTimeExistingGalleries(companyId: string, signal?: AbortSignal) {
        return this.apiService.apiRequest<IPicTimeGallery[]>({
            method: 'get',
            signal,
            url: `/api/v2/companies/${companyId}/integrations/pictime/galleries`
        });
    }

    getPicTimeWebAdressesAccountData(companyId: string, signal?: AbortSignal) {
        // trigger pic time account data fetching but our server return only webAdresses property
        return this.apiService.apiRequest<IPicTimeAccountData['webAdresses']>({
            method: 'get',
            signal,
            url: `/api/v2/companies/${companyId}/integrations/pictime/account`
        });
    }

    getPictimeGalleriesPerEvent(eventId: string) {
        return this.apiService.apiRequest<IPicTimeConnectedGallery[]>({
            method: 'get',
            url: `/api/v2/events/${eventId}/pictime`
        });
    }

    unlinkPicTimeGalleryFromEvent(eventId: string, galleryId: string) {
        return this.apiService.apiRequest<void>({
            method: 'delete',
            url: `/api/v2/events/${eventId}/pictime/${galleryId}`
        });
    }

    syncPicTimeGallery(eventId: string, galleryId: string) {
        return this.apiService.apiRequest<IPicTimeConnectedGallery>({
            method: 'put',
            url: `/api/v2/events/${eventId}/pictime/sync/${galleryId}`
        });
    }

    async connectPicTimeGallery(eventId: string, galleryId: number) {
        return this.apiService.apiRequest<IPicTimeConnectedGallery>({
            method: 'post',
            url: `/api/v2/events/${eventId}/pictime/assign/${galleryId}`
        });
    }

    async createPicTimeGallery(
        eventId: string,
        galleryDetails: TGalleryDetailsPayload
    ) {
        return this.apiService.apiRequest<IPicTimeConnectedGallery>({
            data: galleryDetails,
            method: 'post',
            url: `/api/v2/events/${eventId}/pictime/create_gallery`
        });
    }

    getPicTimeIntegrationInfo(companyId: string) {
        return this.apiService.apiRequest<IPicTimeIntegrationInfoResponse>({
            method: 'get',
            url: `/api/v2/companies/${companyId}/integrations/pictime`
        });
    }

    updateConfiguration(value: boolean, companyId: string, configType: string) {
        return this.apiService.apiRequest<boolean>({
            data: { config_type: configType, state: value },
            method: 'put',
            url: `/api/v2/companies/${companyId}/integrations/pictime/config`
        });
    }

    getPicTimeAuthUrl(redirect_path: string) {
        return this.apiService.apiRequest<IPicTimeAuthUrl>({
            method: 'get',
            url: `/api/v2/integrations/pictime/auth_url?redirect_path=${redirect_path}`
        });
    }

    updateDisplayInFiles = (
        state: boolean,
        eventId: string,
        galleryId: string
    ) => {
        return this.apiService.apiRequest<IPicTimeConnectedGallery>({
            data: { display_in_files: state },
            method: 'put',
            url: `/api/v2/events/${eventId}/pictime/${galleryId}/display_to_client`
        });
    };

    removePicTimeActivityItem(projectId: string, eventId: string) {
        return this.apiService.apiRequest<void>({
            method: 'DELETE',
            url: `/api/v2/events/${eventId}/pictime/${projectId}/remove_activity_item`
        });
    }

    getPicTimeProjectsTemplates(companyId: string) {
        return this.apiService.apiRequest<IPicTimeProjectTemplate[]>({
            method: 'get',
            url: `/api/v2/companies/${companyId}/integrations/pictime/projects_templates`
        });
    }
}
